import { z } from "zod";

import { AuthorSchema } from "@shared/ui/author";

import { createComponentSchemas } from "../utils";
import { ColorIconSchema } from "./common.schema";
import { ArrayOfMediaSchema, StrapiMediaSchema } from "./media.schema";
import { RichTextContentSchema } from "./richText.schema";

export const TextBlockSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  texts: RichTextContentSchema,
});

export const VideoBlockSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  video: StrapiMediaSchema,
  author: z.string().min(1),
  authorUrl: z.string().min(1),
});

export const GalleryBlockSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  images: ArrayOfMediaSchema,
});

export const QuoteBlockSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  author: z.string().min(1),
  authorPosition: z.string().min(1),
  text: z.string().min(1),
});

export const TableRowsShema = z.object({
  id: z.number(),
  title: z.string().min(1),
});

const RowShema = z.object({
  id: z.number(),
  text: z.string().min(1),
});

export const RowsShema = z.object({
  id: z.number(),
  rowItem: z.array(RowShema).min(0),
});

export const ListItemSchema = z.object({
  id: z.number(),
  text: z.string().min(1),
});

export const TableBlockSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  title: z.string().min(1),
  columnTitle: z.array(TableRowsShema).min(0),
  columnSubtitle: z.array(TableRowsShema).optional().nullable(),
  tabletRows: z.array(RowsShema).min(0),
});

export const AccordionWithImagesSchema = z.object({
  backgroundColor: z.string(),
  id: z.number(),
  title: z.string().min(1),
  description: z.string(),
  image: StrapiMediaSchema,
});

export const AccordionsWithImagesSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  title: z.string().min(1),
  accordion: z.array(AccordionWithImagesSchema),
});

export const AccordionTextSchema = z.object({
  id: z.number(),
  title: z.string().min(1),
  text: z.array(RichTextContentSchema),
});

export const AccordionsTextSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  title: z.string().min(1),
  accordion: z.array(AccordionTextSchema),
});

export const SectionNameSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  name: z.string().min(1),
});

export const ListWithIconSchema = z.object({
  id: z.number(),
  __component: z.string().min(1),
  title: z.string().min(1),
  icon: ColorIconSchema,
  items: z.array(ListItemSchema).min(0),
});

export const ConditionsTableRowSchema = z.object({
  id: z.number(),
  label: z.string().min(1),
  value: RichTextContentSchema,
});

export const ConditionsTableSchema = z.object({
  id: z.number(),
  __component: z.literal("article.conditions-table"),
  title: z.string().min(1),
  rows: z.array(ConditionsTableRowSchema).min(0),
});

export const SlotDetailsSchema = z.object({
  id: z.number().optional(),
  description: z.string().nullable(),
  speaker: AuthorSchema.nullable(),
});

// Time Slot component schema
export const TimeSlotSchema = z.object({
  id: z.number().optional(),
  time: z.string().nullable(),
  slotDetails: SlotDetailsSchema.nullable(),
});

// Sector component schema
export const SectorSchema = z.object({
  id: z.number().optional(),
  sectorName: z.string().nullable(),
  timeSlot: z.array(TimeSlotSchema),
});

// Event Program component schema
export const EventProgramBaseSchema = z.object({
  id: z.number().optional(),
  title: z.string().nullable(),
  sector: z.array(SectorSchema),
});

export const EventProgramSchema = createComponentSchemas(
  "event.event-program",
  EventProgramBaseSchema,
);

export const DocumentFormButtonSchema = z.object({
  id: z.number().optional(),
  text: z.string().min(1),
  link: z.string().optional().nullable(),
});

export const DocumentFormBlockSchema = z.object({
  id: z.number(),
  __component: z.literal("ui.document-form"),
  title: z.string().min(1),
  text: z.string().optional().nullable(),
  document: StrapiMediaSchema.nullable().optional(),
  button: DocumentFormButtonSchema.nullable().optional(),
});

export type DocumentFormBlock = z.infer<typeof DocumentFormBlockSchema>;

export const DynamicZoneItemSchema = z.union([
  TextBlockSchema,
  VideoBlockSchema,
  GalleryBlockSchema,
  QuoteBlockSchema,
  TableBlockSchema,
  AccordionsWithImagesSchema,
  AccordionsTextSchema,
  SectionNameSchema,
  ListWithIconSchema,
  ConditionsTableSchema,
  DocumentFormBlockSchema,
  EventProgramSchema.dynamic,
]);

export type DynamicZoneItem = z.infer<typeof DynamicZoneItemSchema>;

export const DynamicZoneSchema = z.array(DynamicZoneItemSchema).min(0);
export type DynamicZone = z.infer<typeof DynamicZoneSchema>;
